<template>
  <main class="campaign-area followups" :dir="locale === 'en' ? 'ltr' : 'rtl'">
    <h1>{{ words.title }}</h1>
    <p>{{ words.hint }}</p>
    <form class="campaign-panel campaign-grid" @submit.prevent="load(1)">
      <label>{{ words.booking }}<select v-model="status"><option value="">{{ words.all }}</option><option v-for="value in statuses" :key="value" :value="value">{{ words[value] }}</option></select></label>
      <label>{{ words.care }}<select v-model="followupStatus"><option value="">{{ words.all }}</option><option v-for="value in careStatuses" :key="value" :value="value">{{ words[value] }}</option></select></label>
      <button class="primary" :disabled="loading || savingId !== ''">{{ loading ? words.loading : words.filter }}</button>
      <button type="button" :disabled="loading || savingId !== ''" @click="load(page)">{{ words.refresh }}</button>
    </form>
    <p v-if="message" role="alert" class="campaign-message">{{ message }}</p>
    <p role="status" aria-live="polite">{{ loading ? words.loading : notice }}</p>
    <p v-if="loaded && !items.length" role="status">{{ words.empty }}</p>
    <p v-if="loaded && !canUpdate">{{ words.readOnly }}</p>
    <section v-for="row in items" :key="row.id" class="campaign-panel" :aria-label="words.title">
      <dl class="followup-details">
        <div><dt>{{ words.name }}</dt><dd>{{ row.fullName }}</dd></div>
        <div><dt>{{ words.phone }}</dt><dd><bdi dir="ltr">{{ row.phone }}</bdi></dd></div>
        <div><dt>{{ words.doctor }}</dt><dd>{{ row.doctorTitle || (row.doctorId ? `${words.doctorId}: ${row.doctorId}` : words.missing) }}</dd></div>
        <div><dt>{{ words.clinic }}</dt><dd>{{ row.clinicTitle || (row.clinicId ? `${words.clinicId}: ${row.clinicId}` : words.missing) }}</dd></div>
        <div><dt>{{ words.verified }}</dt><dd>{{ date(row.verifiedAt) }}</dd></div>
        <div><dt>{{ words.booking }}</dt><dd><strong>{{ words[row.status] }}</strong></dd></div>
      </dl>
      <p v-if="row.status === 'unknown'">{{ words.unknownHint }}</p>
      <form v-if="canUpdate" class="followup-edit" @submit.prevent="save(row)">
        <label :for="`care-${row.id}`">{{ words.care }}<select :id="`care-${row.id}`" v-model="drafts[row.id]" :disabled="savingId !== ''"><option v-for="value in careStatuses" :key="value" :value="value">{{ words[value] }}</option></select></label>
        <button class="primary" :disabled="savingId !== '' || uncertain[row.id] || drafts[row.id] === row.followupStatus">{{ savingId === row.id ? words.saving : words.save }}</button>
      </form>
      <p v-else>{{ words.care }}: {{ words[row.followupStatus] }}</p>
    </section>
    <nav v-if="loaded && total" class="campaign-actions" :aria-label="words.page">
      <button :disabled="loading || savingId !== '' || page <= 1" @click="load(page - 1)">{{ words.previous }}</button>
      <span>{{ words.page }} {{ page }} · {{ words.total }} {{ total }}</span>
      <button :disabled="loading || savingId !== '' || page * limit >= total" @click="load(page + 1)">{{ words.next }}</button>
    </nav>
  </main>
</template>
<script>
import axios from '@/libs/axios'
import hasFollowupAccess from '@/auth/booking-followup-access'
import messages from './messages'
import '../contentCampaign/campaign.css'

export default {
  data() {
    return {
      items: [], drafts: {}, uncertain: {}, page: 1, limit: 20, total: 0, status: '', followupStatus: '', loading: false, loaded: false, savingId: '', message: '', notice: '', sequence: 0, statuses: ['confirmed', 'rejected', 'unknown', 'submitting'], careStatuses: ['pending', 'contacted', 'closed'],
    }
  },
  computed: {
    locale() { return ['fa', 'en', 'ar'].includes(this.$i18n.locale) ? this.$i18n.locale : 'fa' },
    words() { return messages[this.locale] },
    canUpdate() { return hasFollowupAccess() },
  },
  mounted() { this.load(1) },
  beforeDestroy() { this.sequence += 1; this.items = []; this.drafts = {} },
  methods: {
    date(value) {
      let locale = 'en-GB'
      if (this.locale === 'fa') locale = 'fa-IR'
      if (this.locale === 'ar') locale = 'ar'
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    },
    errorMessage(error, updating = false) {
      const status = error && error.response && error.response.status
      if (status === 401) return this.words.session
      if (status === 403) return this.words.forbidden
      if (status === 503) return this.words.unavailable
      if (status === 409) return this.words.conflict
      return updating ? this.words.updateFailed : this.words.failed
    },
    valid(data, page) {
      return data && data.page === page && data.limit === this.limit && Number.isInteger(data.total) && data.total >= 0 && Array.isArray(data.items) && data.items.length <= this.limit && new Set(data.items.map(row => row.id)).size === data.items.length && data.items.every(row => /^[a-f\d]{8}-[a-f\d]{4}-4[a-f\d]{3}-[89ab][a-f\d]{3}-[a-f\d]{12}$/i.test(row.id) && typeof row.fullName === 'string' && typeof row.phone === 'string' && this.statuses.includes(row.status) && this.careStatuses.includes(row.followupStatus) && Number.isInteger(row.careRevision) && Number.isFinite(Date.parse(row.verifiedAt)))
    },
    async load(page) {
      if (this.savingId) return
      this.sequence += 1
      const { sequence } = this
      this.items = []; this.drafts = {}; this.uncertain = {}; this.loaded = false; this.message = ''; this.notice = ''
      if (!hasFollowupAccess()) { this.message = this.words.forbidden; return }
      this.loading = true
      try {
        const params = { page, limit: this.limit, lang: this.locale }
        if (this.status) params.status = this.status
        if (this.followupStatus) params.followupStatus = this.followupStatus
        const { data } = await axios.get('booking-followups', { params, timeout: 10000 })
        if (sequence !== this.sequence) return
        if (!this.valid(data, page)) throw new Error('INVALID_FOLLOWUPS')
        this.items = data.items; this.total = data.total; this.page = page; this.loaded = true
        this.drafts = Object.fromEntries(data.items.map(row => [row.id, row.followupStatus]))
      } catch (error) { if (sequence === this.sequence) this.message = this.errorMessage(error) } finally { if (sequence === this.sequence) this.loading = false }
    },
    async save(row) {
      if (this.savingId || this.uncertain[row.id] || !this.canUpdate || !this.careStatuses.includes(this.drafts[row.id]) || this.drafts[row.id] === row.followupStatus) return
      const { sequence } = this
      this.savingId = row.id; this.message = ''; this.notice = ''
      try {
        const { data } = await axios.patch(`booking-followups/${row.id}/followup`, { followupStatus: this.drafts[row.id], careRevision: row.careRevision }, { timeout: 10000 })
        if (sequence !== this.sequence) return
        if (!data || data.id !== row.id || !this.careStatuses.includes(data.followupStatus) || !Number.isInteger(data.careRevision) || data.careRevision <= row.careRevision) throw new Error('INVALID_UPDATE')
        this.items = this.items.map(item => (item.id === row.id
          ? { ...item, followupStatus: data.followupStatus, careRevision: data.careRevision }
          : item))
        this.$set(this.drafts, row.id, data.followupStatus)
        this.notice = this.words.saved
      } catch (error) {
        if (sequence === this.sequence) {
          this.message = this.errorMessage(error, true)
          this.$set(this.uncertain, row.id, true)
          if (error.response && [401, 403].includes(error.response.status)) { this.items = []; this.drafts = {}; this.loaded = false }
        }
      } finally { if (sequence === this.sequence) this.savingId = '' }
    },
  },
}
</script>
<style scoped>
.followups { text-align: start; }
.followup-details { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.followup-details dd { margin: 4px 0 0; overflow-wrap: anywhere; }
.followup-edit { display: flex; align-items: end; gap: 12px; flex-wrap: wrap; }
.followup-edit label { flex: 1; min-width: 180px; }
@media (max-width: 600px) { .followup-details { grid-template-columns: 1fr; } .followup-edit button { width: 100%; } }
</style>
